import "@/App.css";
import "@/index.css";
import React, { Suspense, lazy, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider } from "@/context/AuthContext";
import { CurrencyProvider } from "@/context/CurrencyContext";
import { Toaster } from "@/components/ui/sonner";
import PublicLayout from "@/components/layout/PublicLayout";
import ProtectedRoute from "@/components/ProtectedRoute";
import CookieConsent from "@/components/CookieConsent";
import PageViewTracker from "@/components/PageViewTracker";
import ScrollToTop from "@/components/ScrollToTop";
import BackToTop from "@/components/BackToTop";
import ErrorBoundary from "@/components/ErrorBoundary";
import CommandPalette from "@/components/CommandPalette";
import PWAInstallPrompt from "@/components/PWAInstallPrompt";

// ---------------------------------------------------------------------------
// Route splitting (Phase 1 perf hardening)
// Eager: only the absolute first-paint pages (Home, Login, NotFound,
// SubmitRFQ — the highest-converting paths).
// Lazy: everything else, downloaded on-demand. Slashes initial JS bundle.
// ---------------------------------------------------------------------------
import Home from "@/pages/Home";
import Login from "@/pages/Login";
import NotFound from "@/pages/NotFound";
import SubmitRFQ from "@/pages/SubmitRFQ";
import AuthCallback from "@/pages/AuthCallback";

// --- Public marketing site (mid-funnel) ---
const About = lazy(() => import("@/pages/About"));
const WhyFortune = lazy(() => import("@/pages/WhyFortune"));
const Process = lazy(() => import("@/pages/Process"));
const Services = lazy(() => import("@/pages/Services"));
const Fees = lazy(() => import("@/pages/Fees"));
const Corridors = lazy(() => import("@/pages/Corridors"));
const CorridorDetail = lazy(() => import("@/pages/CorridorDetail"));
const ServiceDetail = lazy(() => import("@/pages/ServiceDetail"));
const Clusters = lazy(() => import("@/pages/Clusters"));
const ChinaTrips = lazy(() => import("@/pages/ChinaTrips"));
const DDPPage = lazy(() => import("@/pages/DDPPage"));
const BookConsultation = lazy(() => import("@/pages/BookConsultation"));
const Contact = lazy(() => import("@/pages/Contact"));
const Tools = lazy(() => import("@/pages/Tools"));
const Insights = lazy(() => import("@/pages/Insights"));
const InsightDetail = lazy(() => import("@/pages/InsightDetail"));
const AuthorProfile = lazy(() => import("@/pages/AuthorProfile"));
const CountryCompliance = lazy(() => import("@/pages/CountryCompliance"));
const TradeGlossary = lazy(() => import("@/pages/TradeGlossary"));
const FAQ = lazy(() => import("@/pages/FAQ"));
const Careers = lazy(() => import("@/pages/Careers"));
const Legal = lazy(() => import("@/pages/Legal"));
const DataRequest = lazy(() => import("@/pages/DataRequest"));
const WeeklyConsolidatedShipping = lazy(() => import("@/pages/fortune-connect/WeeklyConsolidatedShipping"));
const OceanRates = lazy(() => import("@/pages/tools/OceanRates"));
const Register = lazy(() => import("@/pages/Register"));
const SupplierRegister = lazy(() => import("@/pages/SupplierRegister"));
const SetPassword = lazy(() => import("@/pages/SetPassword"));
const ForgotPassword = lazy(() => import("@/pages/ForgotPassword"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const ImportIndex = lazy(() => import("@/pages/ImportIndex"));
const ImportCompliance = lazy(() => import("@/pages/ImportCompliance"));

// --- Authenticated portals ---
const ClientDashboard = lazy(() => import("@/pages/portals/ClientDashboard"));
const RFQDetail = lazy(() => import("@/pages/portals/RFQDetail"));
const SupplierDashboard = lazy(() => import("@/pages/portals/SupplierDashboard"));
const AdminDashboard = lazy(() => import("@/pages/portals/AdminDashboard"));
const AdminCMS = lazy(() => import("@/pages/portals/AdminCMS"));
// AdminSocial — REMOVED per Master Prompt Section B1.2 (Feb 2026)
const AdminUsers = lazy(() => import("@/pages/portals/AdminUsers"));
const AdminLeads = lazy(() => import("@/pages/portals/AdminLeads"));
const AdminLeadDetail = lazy(() => import("@/pages/portals/AdminLeadDetail"));
const ConnectClients = lazy(() => import("@/pages/connect/ConnectClients"));
const ConnectEnquiries = lazy(() => import("@/pages/connect/ConnectEnquiries"));
const ClientDetail = lazy(() => import("@/pages/connect/ClientDetail"));
const Profile = lazy(() => import("@/pages/portals/Profile"));
const ApprovalQueue = lazy(() => import("@/pages/portals/ApprovalQueue"));
const TasksBoard = lazy(() => import("@/pages/portals/TasksBoard"));
const CalendarPage = lazy(() => import("@/pages/portals/CalendarPage"));
const MasterAdmin = lazy(() => import("@/pages/portals/MasterAdmin"));
const Intelligence = lazy(() => import("@/pages/portals/Intelligence"));
const PersonProfile = lazy(() => import("@/pages/portals/PersonProfile"));

// --- Fortune Connect Handoff (2026-06) ---
const TaskManagerTeam = lazy(() => import("@/pages/portals/TaskManagerTeam"));
const VendorDashboard = lazy(() => import("@/pages/portals/VendorDashboard"));
const AnalyticsDashboard = lazy(() => import("@/pages/portals/AnalyticsDashboard"));
// SocialMediaDashboard — REMOVED per Master Prompt Section B1.2 (Feb 2026)
const IntegrationsHealth = lazy(() => import("@/pages/portals/IntegrationsHealth"));

// --- Fortune Khata (Accounts & Client Ledger) ---
const KhataSettings = lazy(() => import("@/pages/khata/KhataSettings"));
const KhataClients = lazy(() => import("@/pages/khata/KhataClients"));
const KhataLedger = lazy(() => import("@/pages/khata/KhataLedger"));
const KhataDashboard = lazy(() => import("@/pages/khata/KhataDashboard"));
const KhataAgeing = lazy(() => import("@/pages/khata/KhataAgeing"));
const KhataClientFile = lazy(() => import("@/pages/khata/KhataClientFile"));

// --- Fortune Connect operational suite ---
const ConnectDashboard = lazy(() => import("@/pages/connect/ConnectDashboard"));
const ConnectProjects = lazy(() => import("@/pages/connect/ConnectProjects"));
const ConnectExpenses = lazy(() => import("@/pages/connect/ConnectExpenses"));
const ConnectAdvances = lazy(() => import("@/pages/connect/ConnectAdvances"));
// ConnectLogs — REMOVED per B1.3 (merged into ConnectAudit)
const ConnectTrips = lazy(() => import("@/pages/connect/ConnectTrips"));
// ConnectCalendar — REMOVED per B1.3 (merged into /portal/calendar)
const ConnectLedger = lazy(() => import("@/pages/connect/ConnectLedger"));
const ConnectTreasury = lazy(() => import("@/pages/connect/ConnectTreasury"));
const ConnectTeamBalances = lazy(() => import("@/pages/connect/ConnectTeamBalances"));
const ConnectPersonalLedger = lazy(() => import("@/pages/connect/ConnectPersonalLedger"));
const DigestPreview = lazy(() => import("@/pages/connect/DigestPreview"));
const ConnectProfitability = lazy(() => import("@/pages/connect/ConnectProfitability"));
// Referrals reconnected + rebuilt (Phase 2 Completion, Feb 2026) — was
// removed per Master Prompt B1.3, now tied into the Phase 1 Entity/Account
// layer per explicit user request.
const ConnectReferrals = lazy(() => import("@/pages/connect/ConnectReferrals"));
const ConnectAudit = lazy(() => import("@/pages/connect/ConnectAudit"));
const ConnectSuppliers = lazy(() => import("@/pages/connect/ConnectSuppliers"));
const ConnectNotifications = lazy(() => import("@/pages/connect/ConnectNotifications"));
// CLIENT 360 (Oct 2026) — simplified hub pages
const ConnectHome = lazy(() => import("@/pages/connect/ConnectHome"));
const ConnectSettings = lazy(() => import("@/pages/connect/ConnectSettings"));
const ConnectDocuments = lazy(() => import("@/pages/connect/ConnectDocuments"));
// BlogApprovals — REMOVED per Master Prompt B1.3 (dependent on removed B1.1 AI blog drafting)

// Minimal route-change loading state. Avoids spinners on fast nav.
function RouteFallback() {
  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        minHeight: "60vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "#6B7280",
        fontSize: "13px",
        letterSpacing: "0.14em",
        textTransform: "uppercase",
      }}
    >
      Loading…
    </div>
  );
}

// Detect Emergent Google Auth callback fragment BEFORE any protected route runs.
// This must be a synchronous check during render — useEffect runs too late and
// causes race conditions with ProtectedRoute / AuthProvider.
function AppRouter() {
  const location = useLocation();

  // SEO: noindex all authenticated / private routes (login, portals, admin, auth
  // flows) so they never enter search indexes. Public pages stay indexable.
  useEffect(() => {
    const PRIVATE = ["/login", "/register", "/forgot-password", "/reset-password",
      "/set-password", "/portal", "/admin", "/auth", "/insights"];
    const isPrivate = PRIVATE.some((p) => location.pathname.startsWith(p));
    let tag = document.getElementById("fs-robots-dynamic");
    if (isPrivate) {
      if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute("name", "robots");
        tag.setAttribute("id", "fs-robots-dynamic");
        document.head.appendChild(tag);
      }
      tag.setAttribute("content", "noindex,nofollow");
    } else if (tag) {
      tag.remove();
    }
  }, [location.pathname]);

  if (location.hash && location.hash.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Suspense fallback={<RouteFallback />}>
      <Routes>
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route element={<PublicLayout />}>
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<About />} />
          <Route path="/why-fortune" element={<WhyFortune />} />
          <Route path="/process" element={<Process />} />
          <Route path="/services" element={<Services />} />
          <Route path="/services/:slug" element={<ServiceDetail />} />
          <Route path="/fees" element={<Fees />} />
          <Route path="/corridors" element={<Corridors />} />
          <Route path="/corridors/:slug" element={<CorridorDetail />} />
          <Route path="/clusters" element={<Clusters />} />
          <Route path="/china-business-trips" element={<ChinaTrips />} />
          <Route path="/ddp" element={<DDPPage />} />
          <Route path="/submit-rfq" element={<SubmitRFQ />} />
          <Route path="/book-consultation" element={<BookConsultation />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/tools" element={<Tools />} />
          <Route path="/tools/:tool" element={<Tools />} />
          <Route path="/insights" element={<Insights />} />
          <Route path="/insights/author/:slug" element={<AuthorProfile />} />
          <Route path="/insights/:slug" element={<InsightDetail />} />
          <Route path="/import-from-china/:country" element={<CountryCompliance />} />
          <Route path="/import" element={<ImportIndex />} />
          <Route path="/import/:product/from-china/to/:country" element={<ImportCompliance />} />
          <Route path="/glossary" element={<TradeGlossary />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/careers" element={<Careers />} />
          <Route path="/legal/data-request" element={<DataRequest />} />
          <Route path="/legal/:slug" element={<Legal />} />
          <Route path="/fortune-connect/weekly-consolidated-shipping" element={<WeeklyConsolidatedShipping />} />
          <Route path="/tools/ocean-rates" element={<OceanRates />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/suppliers/register" element={<SupplierRegister />} />
        </Route>

        <Route path="/portal/client" element={<ProtectedRoute roles={["client"]}><ClientDashboard /></ProtectedRoute>} />
        {/* /portal/supplier — REMOVED per B1.3. Legacy 'supplier' role now redirects to
            the unified /portal/vendor per Master Prompt Part 0 (Vendor is the canonical
            counterparty name; supplier was legacy terminology). */}
        <Route path="/portal/supplier" element={<Navigate to="/portal/vendor" replace />} />
        <Route path="/portal/admin" element={<Navigate to="/portal/home" replace />} />
        <Route path="/portal/admin/cms" element={<ProtectedRoute roles={["super_admin", "admin", "management"]}><AdminCMS /></ProtectedRoute>} />
        {/* /portal/admin/social — REMOVED per Master Prompt Section B1.2 (Feb 2026) */}
        <Route path="/portal/admin/users" element={<ProtectedRoute roles={["super_admin", "admin", "management"]}><AdminUsers /></ProtectedRoute>} />
        <Route path="/portal/leads" element={<ProtectedRoute roles={["super_admin", "admin", "management", "sales", "procurement"]}><AdminLeads /></ProtectedRoute>} />
        <Route path="/portal/leads/:leadId" element={<ProtectedRoute roles={["super_admin", "admin", "management", "sales", "procurement"]}><AdminLeadDetail /></ProtectedRoute>} />
        <Route path="/portal/connect/clients" element={<ProtectedRoute roles={["super_admin","admin","management","finance","sales","procurement"]}><ConnectClients /></ProtectedRoute>} />
        <Route path="/portal/connect/enquiries" element={<ProtectedRoute roles={["super_admin","admin","management","finance","sales","procurement"]}><ConnectEnquiries /></ProtectedRoute>} />
        <Route path="/portal/connect/clients/:clientId" element={<ProtectedRoute roles={["super_admin","admin","management","finance","sales","procurement"]}><ClientDetail /></ProtectedRoute>} />
        <Route path="/set-password" element={<SetPassword />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />

        <Route path="/portal/rfq/:rfqId" element={<ProtectedRoute roles={["super_admin","admin","management","finance","sales","procurement","qc","client","supplier","vendor"]}><RFQDetail /></ProtectedRoute>} />
        {/* CLIENT 360 — simplified Home dashboard (replaces Admin/Connect landing) */}
        <Route path="/portal/home" element={<ProtectedRoute roles={["super_admin","admin","management","finance","sales","procurement","qc","client"]}><ConnectHome /></ProtectedRoute>} />
        <Route path="/portal/settings" element={<ProtectedRoute roles={["super_admin","admin","management"]}><ConnectSettings /></ProtectedRoute>} />
        <Route path="/portal/documents" element={<ProtectedRoute roles={["super_admin","admin","management","finance","sales","procurement","qc","client"]}><ConnectDocuments /></ProtectedRoute>} />
        <Route path="/portal/connect" element={<Navigate to="/portal/home" replace />} />
        <Route path="/portal/connect/projects" element={<ProtectedRoute roles={["super_admin","admin","management","finance","sales","procurement","qc","client"]}><ConnectProjects /></ProtectedRoute>} />
        <Route path="/portal/connect/expenses" element={<ProtectedRoute roles={["super_admin","admin","management","finance","sales","procurement","qc","client"]}><ConnectExpenses /></ProtectedRoute>} />
        <Route path="/portal/connect/advances" element={<ProtectedRoute roles={["super_admin","admin","management","finance","sales","procurement","qc","client"]}><ConnectAdvances /></ProtectedRoute>} />
        {/* /portal/connect/logs — REMOVED per B1.3 (merged into /portal/connect/audit). Redirect keeps existing bookmarks alive. */}
        <Route path="/portal/connect/logs" element={<Navigate to="/portal/connect/audit" replace />} />
        <Route path="/portal/connect/trips" element={<ProtectedRoute roles={["super_admin","admin","management","finance","sales","procurement","qc","client"]}><ConnectTrips /></ProtectedRoute>} />
        {/* /portal/connect/calendar — REMOVED per B1.3 (merged into /portal/calendar). */}
        <Route path="/portal/connect/calendar" element={<Navigate to="/portal/calendar" replace />} />
        <Route path="/portal/connect/ledger" element={<ProtectedRoute roles={["super_admin","admin","management","finance","sales","procurement","qc","client"]}><ConnectLedger /></ProtectedRoute>} />
        <Route path="/portal/connect/treasury" element={<ProtectedRoute roles={["super_admin","admin","management","finance","sales","procurement","qc"]}><ConnectTreasury /></ProtectedRoute>} />
        <Route path="/portal/connect/team-balances" element={<ProtectedRoute roles={["super_admin","admin","management","finance","sales","procurement","qc"]}><ConnectTeamBalances /></ProtectedRoute>} />
        <Route path="/portal/connect/personal-ledger" element={<ProtectedRoute roles={["super_admin","admin","management","finance","sales","procurement","qc"]}><ConnectPersonalLedger /></ProtectedRoute>} />
        <Route path="/portal/connect/digest-preview" element={<ProtectedRoute roles={["super_admin"]}><DigestPreview /></ProtectedRoute>} />
        {/* /portal/connect/referrals — reconnected + rebuilt (Phase 2 Completion). */}
        <Route path="/portal/connect/referrals" element={<ProtectedRoute roles={["super_admin","admin","management","finance","sales","procurement"]}><ConnectReferrals /></ProtectedRoute>} />
        <Route path="/portal/connect/profitability" element={<ProtectedRoute roles={["super_admin","admin","management"]}><ConnectProfitability /></ProtectedRoute>} />
        <Route path="/portal/connect/audit" element={<ProtectedRoute roles={["super_admin","admin","management"]}><ConnectAudit /></ProtectedRoute>} />
        <Route path="/portal/connect/suppliers" element={<ProtectedRoute roles={["super_admin","admin","management","procurement"]}><ConnectSuppliers /></ProtectedRoute>} />
        <Route path="/portal/connect/notifications" element={<ProtectedRoute roles={["super_admin","admin","management","finance","sales","procurement","qc","client"]}><ConnectNotifications /></ProtectedRoute>} />
        {/* /portal/connect/blog-approvals — REMOVED per B1.3 (AI blog drafting deprecated in B1.1). */}
        <Route path="/portal/connect/blog-approvals" element={<Navigate to="/portal/connect" replace />} />

        {/* Fortune Khata — Accounts & Client Ledger (own khata_* collections) */}
        <Route path="/portal/khata" element={<ProtectedRoute roles={["super_admin","admin","management","finance"]}><KhataDashboard /></ProtectedRoute>} />
        <Route path="/portal/khata/clients" element={<ProtectedRoute roles={["super_admin","admin","management","finance"]}><KhataClients /></ProtectedRoute>} />
        <Route path="/portal/khata/clients/:id" element={<ProtectedRoute roles={["super_admin","admin","management","finance"]}><KhataClientFile /></ProtectedRoute>} />
        <Route path="/portal/khata/ledger" element={<ProtectedRoute roles={["super_admin","admin","management","finance"]}><KhataLedger /></ProtectedRoute>} />
        <Route path="/portal/khata/ageing" element={<ProtectedRoute roles={["super_admin","admin","management","finance"]}><KhataAgeing /></ProtectedRoute>} />
        <Route path="/portal/khata/settings" element={<ProtectedRoute roles={["super_admin","admin","management","finance"]}><KhataSettings /></ProtectedRoute>} />
        <Route path="/portal/khata/profitability" element={<ProtectedRoute roles={["super_admin","admin","management"]}><ConnectProfitability /></ProtectedRoute>} />


        <Route path="/portal/profile" element={<ProtectedRoute roles={["admin","super_admin","partner","management","finance","sales","procurement","qc","operations","client","supplier","vendor"]}><Profile /></ProtectedRoute>} />
        <Route path="/portal/approvals" element={<ProtectedRoute roles={["admin","super_admin","partner","management","finance","sales","procurement","qc","operations","client","supplier","vendor"]}><ApprovalQueue /></ProtectedRoute>} />
        <Route path="/portal/tasks" element={<ProtectedRoute roles={["admin","super_admin","partner","management","finance","sales","procurement","qc","operations","client","supplier","vendor"]}><TaskManagerTeam /></ProtectedRoute>} />
        <Route path="/portal/calendar" element={<ProtectedRoute roles={["admin","super_admin","partner","management","finance","sales","procurement","qc","operations","client","supplier","vendor"]}><CalendarPage /></ProtectedRoute>} />
        <Route path="/portal/master" element={<ProtectedRoute roles={["super_admin","admin"]}><MasterAdmin /></ProtectedRoute>} />
        <Route path="/portal/intelligence" element={<ProtectedRoute roles={["super_admin","admin","management","finance","sales","procurement","qc"]}><Intelligence /></ProtectedRoute>} />
        <Route path="/portal/people/:userId" element={<ProtectedRoute roles={["super_admin","admin","management","finance","sales","procurement","qc"]}><PersonProfile /></ProtectedRoute>} />
        <Route path="/portal/people/by-email/:email" element={<ProtectedRoute roles={["super_admin","admin","management","finance","sales","procurement","qc"]}><PersonProfile /></ProtectedRoute>} />

        {/* Fortune Connect Handoff (2026-06) */}
        <Route path="/portal/vendor" element={<ProtectedRoute roles={["supplier","vendor"]}><VendorDashboard /></ProtectedRoute>} />
        <Route path="/portal/analytics" element={<ProtectedRoute roles={["super_admin","admin","management"]}><AnalyticsDashboard /></ProtectedRoute>} />
        {/* /portal/social — REMOVED per Master Prompt Section B1.2 (Feb 2026) */}
        <Route path="/portal/admin/integrations" element={<ProtectedRoute roles={["super_admin","admin"]}><IntegrationsHealth /></ProtectedRoute>} />

        <Route element={<PublicLayout />}>
          <Route path="*" element={<NotFound />} />
        </Route>
      </Routes>
    </Suspense>
  );
}

export default function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <CurrencyProvider>
          <BrowserRouter>
          <ErrorBoundary>
            <ScrollToTop />
            <PageViewTracker />
            <CommandPalette />
            <AppRouter />
            <CookieConsent />
            <BackToTop />
            <PWAInstallPrompt />
            <Toaster position="top-right" richColors />
          </ErrorBoundary>
        </BrowserRouter>
        </CurrencyProvider>
      </AuthProvider>
    </HelmetProvider>
  );
}
